/**
 * Embed Owner Profile Provider
 * Fetches and provides the widget owner's profile data based on the id in config
 */

import React, { createContext, useContext, useEffect, useState } from 'react';

import { debugLog } from '../debug';
import { useEmbedAuth } from './EmbedAuthProvider';
import { useEmbedConfig } from './EmbedConfigProvider';

// ============================================================================
// Types
// ============================================================================

export interface OwnerProfile {
  id: string;
  coreUserId: string;
  Name: string;
  Email?: string;
  DisplayPic?: string;
  Headline?: string;
  Handle?: string;
}

interface OwnerProfileContextValue {
  ownerProfile: OwnerProfile | null;
  isLoading: boolean;
  error: string | null;
}

// ============================================================================
// API Functions
// ============================================================================

// Extract handle from path like /p/{handle} or /p/{handle}?pov=xxx
function extractHandleFromPath(path?: string): string | null {
  if (!path) return null;
  const match = path.match(/^\/p\/([^/?]+)/);
  return match ? match[1] : null;
}

async function fetchOwnerProfile(
  appBaseUrl: string,
  token: string,
  userId?: string,
  ownerHandle?: string,
  path?: string,
): Promise<OwnerProfile | null> {
  try {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'X-Bolder-Widget': 'true',
      'X-Bolder-Widget-Origin': typeof window !== 'undefined' ? window.location.origin : '',
      Authorization: `Bearer ${token}`,
    };

    // Priority: ownerHandle from config > handle from path > fetch by userId
    const handle = ownerHandle || extractHandleFromPath(path);

    if (handle) {
      // Use the public profile endpoint with handle
      const url = `${appBaseUrl}/api/widget-proxy/api/profile/handle/user-details/${handle}`;
      debugLog('[EmbedOwnerProfile] Fetching profile by handle from:', url);

      const response = await fetch(url, { method: 'GET', headers });
      debugLog('[EmbedOwnerProfile] Response status:', response.status);

      if (response.ok) {
        const result = await response.json();
        debugLog('[EmbedOwnerProfile] Profile result:', result);

        const profileDetails = result?.ProfileDetails;
        if (profileDetails) {
          return {
            id: profileDetails.id || profileDetails._id || userId || '',
            coreUserId: profileDetails.coreUserId || userId || '',
            Name: profileDetails.Name || '',
            Email: profileDetails.Email,
            DisplayPic: profileDetails.DisplayPic,
            Headline: profileDetails.Headline,
            Handle: profileDetails.Handle || handle,
          };
        }
      }
    }

    // Fallback: try to get handle by userId first, then fetch profile
    if (userId) {
      const handleUrl = `${appBaseUrl}/api/widget-proxy/api/profile/handle/${userId}`;
      debugLog('[EmbedOwnerProfile] Fetching handle from:', handleUrl);

      const handleResponse = await fetch(handleUrl, { method: 'GET', headers });

      if (handleResponse.ok) {
        const handleResult = await handleResponse.json();
        const fetchedHandle = handleResult?.handle;

        if (fetchedHandle) {
          const profileUrl = `${appBaseUrl}/api/widget-proxy/api/profile/handle/user-details/${fetchedHandle}`;
          debugLog('[EmbedOwnerProfile] Fetching profile from:', profileUrl);

          const profileResponse = await fetch(profileUrl, { method: 'GET', headers });

          if (profileResponse.ok) {
            const profileResult = await profileResponse.json();
            const profileDetails = profileResult?.ProfileDetails;

            if (profileDetails) {
              return {
                id: profileDetails.id || profileDetails._id || userId,
                coreUserId: profileDetails.coreUserId || userId,
                Name: profileDetails.Name || '',
                Email: profileDetails.Email,
                DisplayPic: profileDetails.DisplayPic,
                Headline: profileDetails.Headline,
                Handle: profileDetails.Handle || fetchedHandle,
              };
            }
          }
        }
      }
    }

    debugLog('[EmbedOwnerProfile] Could not fetch profile');
    return null;
  } catch (error) {
    console.error('[EmbedOwnerProfile] Error fetching owner profile:', error);
    return null;
  }
}

// ============================================================================
// Context
// ============================================================================

const OwnerProfileContext = createContext<OwnerProfileContextValue>({
  ownerProfile: null,
  isLoading: false,
  error: null,
});

// ============================================================================
// Provider
// ============================================================================

export interface EmbedOwnerProfileProviderProps {
  children: React.ReactNode;
}

export const EmbedOwnerProfileProvider: React.FC<EmbedOwnerProfileProviderProps> = ({ children }) => {
  const { id, ownerHandle, appBaseUrl, path } = useEmbedConfig();
  const { guestToken, jwt, guestTokenLoading } = useEmbedAuth();
  const [ownerProfile, setOwnerProfile] = useState<OwnerProfile | null>(null);
  // Start loading as true if id or ownerHandle is present - don't show fallback initially
  const [isLoading, setIsLoading] = useState(!!id || !!ownerHandle);
  const [error, setError] = useState<string | null>(null);

  // Use jwt if available, otherwise use guestToken
  const token = jwt || guestToken;

  debugLog(
    '[EmbedOwnerProfile] Provider mounted, id:',
    id,
    'ownerHandle:',
    ownerHandle,
    'token:',
    !!token,
    'guestTokenLoading:',
    guestTokenLoading,
  );

  useEffect(() => {
    debugLog(
      '[EmbedOwnerProfile] useEffect triggered, id:',
      id,
      'ownerHandle:',
      ownerHandle,
      'token:',
      !!token,
      'guestTokenLoading:',
      guestTokenLoading,
    );

    // Wait for guest token to load
    if (guestTokenLoading) {
      debugLog('[EmbedOwnerProfile] Waiting for guest token...');
      return;
    }

    if ((!id && !ownerHandle) || !appBaseUrl) {
      debugLog('[EmbedOwnerProfile] Missing id/ownerHandle or appBaseUrl, skipping fetch');
      setOwnerProfile(null);
      setIsLoading(false);
      return;
    }

    if (!token) {
      debugLog('[EmbedOwnerProfile] No token available, skipping fetch');
      setOwnerProfile(null);
      setIsLoading(false);
      return;
    }

    const loadOwnerProfile = async () => {
      debugLog('[EmbedOwnerProfile] Starting to load owner profile');
      setIsLoading(true);
      setError(null);

      try {
        const profile = await fetchOwnerProfile(appBaseUrl, token, id, ownerHandle, path);
        debugLog('[EmbedOwnerProfile] Loaded profile:', profile);
        setOwnerProfile(profile);
      } catch (err) {
        console.error('[EmbedOwnerProfile] Failed to load owner profile:', err);
        setError('Failed to load owner profile');
      } finally {
        setIsLoading(false);
      }
    };

    loadOwnerProfile();
  }, [id, ownerHandle, appBaseUrl, path, token, guestTokenLoading]);

  return (
    <OwnerProfileContext.Provider value={{ ownerProfile, isLoading, error }}>
      {children}
    </OwnerProfileContext.Provider>
  );
};

// ============================================================================
// Hook
// ============================================================================

export const useOwnerProfile = (): OwnerProfileContextValue => {
  return useContext(OwnerProfileContext);
};
